import React from 'react';
import { ClearableError } from './shared';
import { ErrorMessage } from '../basic/ErrorMessage';
import { pushErrorEventToDataLayer } from '../../analytics/pushErrorEventToDataLayer';

interface ErrorBoundaryPoliteProps {
  errorThresholdMs?: number;
}

interface ErrorBoundaryPoliteState {
  error: ClearableError;
  lastError: ClearableError;
  errorAt: number;
  lastErrorAt: number;
}

/**
 * ErrorBoundaryPolite tries to be non-intrusive by showing a small toast instead of a full
 * error page.
 */
export class ErrorBoundaryPolite extends React.Component<
  ErrorBoundaryPoliteProps,
  ErrorBoundaryPoliteState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      error: null,
      lastError: null,
      errorAt: Date.now(),
      lastErrorAt: Date.now()
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { error, errorAt: Date.now() };
  }

  componentDidCatch(error: Error) {
    pushErrorEventToDataLayer({
      error_type: 'unknown_error',
      error_category: 'unknown_error_category',
      error_code: 'REACT_ERROR_BOUNDARY_POLITE',
      error_description: `React render error caught by polite boundary: ${error.message ||
        'Something went wrong'}.`,
      error_location: 'ErrorBoundaryPolite.componentDidCatch',
      error_object: error
    });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    const errToast = (
      <ErrorMessage
        key="error-message"
        onDismiss={() => {
          this.setState(old => {
            return {
              ...old,
              error: null
            };
          });
        }}
        errorMessage={error?.message || 'Something went wrong'}
        isShown={!!error}
      />
    );

    return (
      <>
        {errToast}
        {children}
      </>
    );
  }
}
